import { AssistChip } from '@sixui/ui';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { UpgradeButton } from '~/components/molecules/UpgradeButton';
import { useAppCurrentFeaturePlanQuery } from '~/lib/api/modules/Apps/hooks/useAppCurrentFeaturePlanQuery';
import { AppPricingTableFeature } from './AppPricingTableFeature';
import { AppPricingTablePlan } from './AppPricingTablePlan';

export type IAppPricingTableProps = IContainerProps & {
  appId: string;
};

const styles = stylex.create({
  locked: {
    color: colorSchemeTokens.error,
  },
  unlocked: {
    color: 'green',
  },
  table: {
    display: 'grid',
    gridTemplateColumns: '1fr repeat(5, 1fr)',
    gap: '1rem',
    columnGap: '2rem',
    alignItems: 'center',
  },
});

export const AppPricingTable: React.FC<IAppPricingTableProps> = (props) => {
  const { sx, appId } = props;
  const { t } = useTranslation();

  const currentFeaturePlanQuery = useAppCurrentFeaturePlanQuery({
    appId,
  });
  const plan = currentFeaturePlanQuery.data;

  return (
    <ContentBlock sx={sx}>
      <ContentBlock.Suspense
        loading={currentFeaturePlanQuery.isLoading}
        error={currentFeaturePlanQuery.error}
        first
      >
        <ContentBlock.Body first>
          <div {...stylex.props(styles.table)}>
            <div />
            <AppPricingTablePlan title={t('AppPricingTable.plans.free.label')}>
              {plan?.tags?.includes('free') && (
                <AssistChip label={t('AppPricingTable.currentPlan')} />
              )}
            </AppPricingTablePlan>
            <AppPricingTablePlan title={t('AppPricingTable.plans.basic.label')}>
              <UpgradeButton appId={appId} plan="basic" />
            </AppPricingTablePlan>
            <AppPricingTablePlan title={t('AppPricingTable.plans.pro.label')}>
              <UpgradeButton appId={appId} plan="pro" />
            </AppPricingTablePlan>
            <AppPricingTablePlan
              title={t('AppPricingTable.plans.premium.label')}
            >
              <UpgradeButton appId={appId} plan="premium" />
            </AppPricingTablePlan>
            <AppPricingTablePlan
              title={t('AppPricingTable.plans.entertainment.label')}
            >
              <UpgradeButton appId={appId} plan="entertainment" />
            </AppPricingTablePlan>
            <AppPricingTableFeature
              label={t('AppPricingTable.features.contentManagement.label')}
              supportingText={t(
                'AppPricingTable.features.contentManagement.supportingText',
              )}
            />
            <AppPricingTableFeature
              label={t('AppPricingTable.features.downloadableMobileApp.label')}
              supportingText={t(
                'AppPricingTable.features.downloadableMobileApp.supportingText',
              )}
            />
            <AppPricingTableFeature
              label={t('AppPricingTable.features.team.label')}
              supportingText={t('AppPricingTable.features.team.supportingText')}
            />
            <AppPricingTableFeature
              label={t('AppPricingTable.features.pushNotifications.label')}
              supportingText={t(
                'AppPricingTable.features.pushNotifications.supportingText',
              )}
            />
            <AppPricingTableFeature
              label={t('AppPricingTable.features.advertisements.label')}
              supportingText={t(
                'AppPricingTable.features.advertisements.supportingText',
              )}
            />
            <AppPricingTableFeature
              label={t('AppPricingTable.features.trainingAndSupport.label')}
              supportingText={t(
                'AppPricingTable.features.trainingAndSupport.supportingText',
              )}
            />
            <AppPricingTableFeature
              label={t('AppPricingTable.features.technicalSupport.label')}
              supportingText={t(
                'AppPricingTable.features.technicalSupport.supportingText',
              )}
              values={{
                free: t('AppPricingTable.features.technicalSupport.under48h'),
                basic: t('AppPricingTable.features.technicalSupport.under24h'),
                pro: t('AppPricingTable.features.technicalSupport.under24h'),
                premium: t('AppPricingTable.features.technicalSupport.24h24h'),
                entertainment: t(
                  'AppPricingTable.features.technicalSupport.24h24h',
                ),
              }}
            />
            <AppPricingTableFeature
              label={t('AppPricingTable.features.activeUsers.label')}
              hideBadges
              values={{
                free: '50',
                basic: '500',
                pro: '10 000',
                premium: '100 000',
                entertainment: t('common.unlimited'),
              }}
            />
            <AppPricingTableFeature
              label={t('AppPricingTable.features.badges.label')}
              supportingText={t(
                'AppPricingTable.features.badges.supportingText',
              )}
              plan="pro"
            />
            <AppPricingTableFeature
              label={t('AppPricingTable.features.chat.label')}
              supportingText={t('AppPricingTable.features.chat.supportingText')}
              plan="pro"
            />
            <AppPricingTableFeature
              label={t('AppPricingTable.features.crowd.label')}
              supportingText={t(
                'AppPricingTable.features.crowd.supportingText',
              )}
              plan="pro"
            />
            <AppPricingTableFeature
              label={t('AppPricingTable.features.liveStreams.label')}
              supportingText={t(
                'AppPricingTable.features.liveStreams.supportingText',
              )}
              plan="pro"
              values={{
                free: null,
                basic: null,
                pro: t('AppPricingTable.features.liveStreams.10hPerWeek'),
                premium: t('AppPricingTable.features.liveStreams.15hPerWeek'),
                entertainment: t('common.unlimited'),
              }}
            />
            <AppPricingTableFeature
              label={t('AppPricingTable.features.appTabs.label')}
              supportingText={t(
                'AppPricingTable.features.appTabs.supportingText',
              )}
              plan="pro"
            />
            <AppPricingTableFeature
              label={t('AppPricingTable.features.playlists.label')}
              supportingText={t(
                'AppPricingTable.features.playlists.supportingText',
              )}
              plan="pro"
            />
            <AppPricingTableFeature
              label={t('AppPricingTable.features.polls.label')}
              supportingText={t(
                'AppPricingTable.features.polls.supportingText',
              )}
              plan="pro"
            />
            <AppPricingTableFeature
              label={t('AppPricingTable.features.iapPolls.label')}
              supportingText={t(
                'AppPricingTable.features.iapPolls.supportingText',
              )}
              plan="pro"
            />
            <AppPricingTableFeature
              label={t('AppPricingTable.features.leaderboardWp.label')}
              supportingText={t(
                'AppPricingTable.features.leaderboardWp.supportingText',
              )}
              plan="pro"
            />
            <AppPricingTableFeature
              label={t('AppPricingTable.features.appTheme.label')}
              supportingText={t(
                'AppPricingTable.features.appTheme.supportingText',
              )}
              plan="pro"
            />
            <AppPricingTableFeature
              label={t('AppPricingTable.features.translations.label')}
              supportingText={t(
                'AppPricingTable.features.translations.supportingText',
              )}
              plan="pro"
            />
            <AppPricingTableFeature
              label={t('AppPricingTable.features.startupScreen.label')}
              supportingText={t(
                'AppPricingTable.features.startupScreen.supportingText',
              )}
              plan="pro"
            />
            <AppPricingTableFeature
              label={t('AppPricingTable.features.developerLicence.label')}
              supportingText={t(
                'AppPricingTable.features.developerLicence.supportingText',
              )}
              hideBadges
              values={{
                free: t('AppPricingTable.features.developerLicence.199OneTime'),
                basic: t('AppPricingTable.features.developerLicence.99OneTime'),
                pro: t('AppPricingTable.features.developerLicence.99OneTime'),
                premium: true,
                entertainment: true,
              }}
            />
            <AppPricingTableFeature
              label={t('AppPricingTable.features.testYourApp.label')}
              supportingText={t(
                'AppPricingTable.features.testYourApp.supportingText',
              )}
            />
            <AppPricingTableFeature
              label={t('AppPricingTable.features.buildYourApp.label')}
              supportingText={t(
                'AppPricingTable.features.buildYourApp.supportingText',
              )}
              hideBadges
              values={{
                free: t('AppPricingTable.features.buildYourApp.149OneTime'),
                basic: true,
                pro: true,
                premium: true,
                entertainment: true,
              }}
            />
            <AppPricingTableFeature
              label={t('AppPricingTable.features.visualAssets.label')}
              supportingText={t(
                'AppPricingTable.features.visualAssets.supportingText',
              )}
              hideBadges
              values={{
                free: t('AppPricingTable.features.visualAssets.299OneTime'),
                basic: t('AppPricingTable.features.visualAssets.299OneTime'),
                pro: t('AppPricingTable.features.visualAssets.299OneTime'),
                premium: true,
                entertainment: true,
              }}
            />
            <AppPricingTableFeature
              label={t(
                'AppPricingTable.features.storesSubmissionAndCompliance.label',
              )}
              supportingText={t(
                'AppPricingTable.features.storesSubmissionAndCompliance.supportingText',
              )}
              hideBadges
              values={{
                free: t(
                  'AppPricingTable.features.storesSubmissionAndCompliance.299OneTime',
                ),
                basic: t(
                  'AppPricingTable.features.storesSubmissionAndCompliance.299OneTime',
                ),
                pro: t(
                  'AppPricingTable.features.storesSubmissionAndCompliance.299OneTime',
                ),
                premium: true,
                entertainment: true,
              }}
            />
            <AppPricingTableFeature
              label={t('AppPricingTable.features.customDevelopments.label')}
              supportingText={t(
                'AppPricingTable.features.customDevelopments.supportingText',
              )}
              plan="premium"
            />
          </div>
        </ContentBlock.Body>
      </ContentBlock.Suspense>
    </ContentBlock>
  );
};
